import { AxisBottom, AxisLeft } from '@visx/axis'
import { localPoint } from '@visx/event'
import { LinearGradient } from '@visx/gradient'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { useParentSize } from '@visx/responsive'
import { scaleLinear } from '@visx/scale'
import { AreaClosed, Bar } from '@visx/shape'
import { defaultStyles, TooltipWithBounds, useTooltip } from '@visx/tooltip'
import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { CalculatedZone } from '../../types/zones'
import { formatPace, formatTimeSeconds } from '../../utils/dateUtils'

interface SplitsChartProps {
  workoutId: string
  hrZones?: CalculatedZone[]
}

interface MetricSample {
  timestamp_seconds: number
  value: number
}

interface Split {
  splitNumber: number
  distance: number
  duration: number
  ascent: number
  descent: number
  avgHeartRate: number
  pace: number // min/km
  startTime: number
  endTime: number
  hrZone: CalculatedZone | null
  hrZoneColor: string | null
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const MetricValue = styled.span`
  font-weight: 700;
`

const tooltipStyles = {
  ...defaultStyles,
  background: theme.colors.cardBackground,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.borderRadius.sm,
  padding: theme.spacing.sm,
  fontSize: theme.typography.xs,
  color: theme.colors.text,
}

const getHRZone = (avgHR: number, hrZones?: CalculatedZone[]): CalculatedZone | null => {
  if (!hrZones || hrZones.length === 0) {
    return null
  }
  console.log('getHRZone', avgHR, hrZones)

  // Find the zone that contains this HR value
  for (const zone of hrZones) {
    if (zone.minValue !== null && zone.maxValue !== null) {
      if (avgHR >= zone.minValue && avgHR < zone.maxValue) {
        return zone
      }
    }
  }
  return null
}

// Helper to determine HR zone color
const getHRZoneColor = (avgHR: number, hrZones?: CalculatedZone[]): string => {
  if (!hrZones || hrZones.length === 0) {
    return '#71CCAD' // Default color
  }

  // Find the zone that contains this HR value
  for (const zone of hrZones) {
    if (zone.minValue !== null && zone.maxValue !== null) {
      if (avgHR >= zone.minValue && avgHR <= zone.maxValue) {
        return zone.color
      }
    }
  }

  // If not in any zone, use the color of the highest zone if above all, or lowest if below
  if (avgHR > (hrZones[hrZones.length - 1]?.maxValue ?? 0)) {
    return hrZones[hrZones.length - 1].color
  }
  return hrZones[0].color
}

// Tooltip content
const TooltipContent = ({ split }: { split: Split }) => (
  <div>
    <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>Split {split.splitNumber}</div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Distance:</span>
      <MetricValue>{split.distance} km</MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Ascent/Descent:</span>
      <MetricValue>
        {split.ascent.toFixed(0)} m / {split.descent.toFixed(0)} m
      </MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Avg Pace:</span>
      <MetricValue>{formatPace(split.pace)} /km</MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Avg Heart Rate:</span>
      <MetricValue>{Math.round(split.avgHeartRate)} bpm</MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Heart Rate Zone:</span>
      <MetricValue>{split.hrZone?.name}</MetricValue>
    </div>
  </div>
)

export const SplitsChart: React.FC<SplitsChartProps> = ({ workoutId, hrZones }) => {
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<Split>()
  const { parentRef, width } = useParentSize({ debounceTime: 150 })
  const [splits, setSplits] = useState<Split[]>([])
  const [altitudeData, setAltitudeData] = useState<{ timestamp: number; value: number }[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch metrics data and calculate splits
  useEffect(() => {
    const loadSplitsData = async () => {
      if (!workoutId) return

      setLoading(true)
      try {
        // Fetch distance and heart rate metrics
        const [distanceResult, hrResult, altitudeResult] = await Promise.all([
          supabase.rpc('get_workout_metrics', {
            p_workout_id: workoutId,
            p_metric_name: 'distance',
            p_downsample_interval: 1,
            p_smoothing_window: 1,
          }),
          supabase.rpc('get_workout_metrics', {
            p_workout_id: workoutId,
            p_metric_name: 'heart_rate',
            p_downsample_interval: 1,
            p_smoothing_window: 1,
          }),
          supabase.rpc('get_workout_metrics', {
            p_workout_id: workoutId,
            p_metric_name: 'altitude',
            p_downsample_interval: 1,
            p_smoothing_window: 1,
          }),
        ])

        if (distanceResult.error || hrResult.error) {
          console.error('Error loading metrics:', distanceResult.error || hrResult.error)
          setLoading(false)
          return
        }

        const distanceData = (distanceResult.data || []) as MetricSample[]
        const hrData = (hrResult.data || []) as MetricSample[]
        const altitudeDataRaw = (altitudeResult.data || []) as MetricSample[]

        if (distanceData.length === 0) {
          setLoading(false)
          return
        }

        // Set altitude data for background
        if (altitudeDataRaw.length > 0) {
          setAltitudeData(
            altitudeDataRaw.map((sample) => ({
              timestamp: sample.timestamp_seconds,
              value: sample.value,
            }))
          )
        }

        // Calculate 1km splits
        const calculatedSplits: Split[] = []
        let splitNumber = 1
        const splitDistance = 1000 // 1km in meters

        // Create a map of HR values by timestamp for quick lookup
        const hrMap = new Map<number, number>()
        for (const sample of hrData) {
          hrMap.set(sample.timestamp_seconds, sample.value)
        }

        let splitStartIndex = 0
        let splitStartDistance = 0
        let splitStartTime = distanceData[0].timestamp_seconds

        for (let i = 0; i < distanceData.length; i++) {
          const currentDistance = distanceData[i].value
          const distanceSinceSplitStart = currentDistance - splitStartDistance

          // Check if we've completed a split
          if (distanceSinceSplitStart >= splitDistance) {
            const splitEndTime = distanceData[i].timestamp_seconds
            const splitDuration = splitEndTime - splitStartTime

            // calculate ascent and descent
            let ascent = 0
            let descent = 0
            for (let j = splitStartIndex; j <= i; j++) {
              if (j < 1) continue
              const prevAltitude = altitudeDataRaw[j - 1]?.value ?? 0
              const altitude = altitudeDataRaw[j]?.value ?? 0
              const heightDiff = altitude - prevAltitude
              if (heightDiff > 0) ascent += Math.abs(heightDiff)
              if (heightDiff < 0) descent += Math.abs(heightDiff)
            }

            // Calculate average heart rate for this split
            let hrSum = 0
            let hrCount = 0
            for (let j = splitStartIndex; j <= i; j++) {
              const hr = hrMap.get(distanceData[j].timestamp_seconds)
              if (hr !== undefined && hr > 0) {
                hrSum += hr
                hrCount++
              }
            }
            const avgHeartRate = hrCount > 0 ? hrSum / hrCount : 0

            // Calculate pace (min/km)
            const pace = splitDuration > 0 ? splitDuration / 60 : 0 // duration is in seconds, convert to minutes

            // Get HR zone color
            const hrZone = getHRZone(Math.round(avgHeartRate), hrZones)

            calculatedSplits.push({
              splitNumber,
              distance: splitNumber, // Distance in km (1, 2, 3, ...)
              duration: splitDuration,
              ascent,
              descent,
              avgHeartRate,
              pace,
              startTime: splitStartTime,
              endTime: splitEndTime,
              hrZone,
              hrZoneColor: hrZone?.color ?? null,
            })

            // Move to next split
            splitNumber++
            splitStartIndex = i
            splitStartDistance = currentDistance
            splitStartTime = splitEndTime
          }
        }
        console.log('Calculated Splits:', calculatedSplits)

        setSplits(calculatedSplits)
      } catch (err) {
        console.error('Error calculating splits:', err)
      } finally {
        setLoading(false)
      }
    }

    loadSplitsData()
  }, [workoutId, hrZones])

  // Render loading state
  const renderContent = () => {
    if (loading) {
      return <LoadingMessage>Loading splits data...</LoadingMessage>
    }

    if (splits.length === 0) {
      return <LoadingMessage>No split data available.</LoadingMessage>
    }

    // Wait for valid width before rendering chart
    if (!width || width === 0) {
      return null
    }

    // Chart dimensions
    const height = 250
    const margin = { top: 20, right: 20, bottom: 40, left: 60 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    // Calculate the time range from the splits
    const minTime = splits.length > 0 ? splits[0].startTime : 0
    const maxTime = splits.length > 0 ? splits[splits.length - 1].endTime : 0

    // Scales
    const xScale = scaleLinear({
      domain: [minTime, maxTime],
      range: [0, innerWidth],
    })

    // Calculate pace range (excluding any zero or invalid paces)
    const validPaces = splits.filter((s) => s.pace > 0).map((s) => s.pace)
    const minPace = Math.min(...validPaces)
    const maxPace = Math.max(...validPaces)
    const pacePadding = Math.max((maxPace - minPace) * 0.1, 0.2)

    // Invert domain so faster pace (lower values) appears higher
    const yScale = scaleLinear({
      domain: [maxPace + pacePadding, Math.max(0, minPace - pacePadding)],
      range: [innerHeight, 0],
    })

    // Altitude scale (for background area)
    let altitudeScale: any = null
    if (altitudeData.length > 0) {
      const altitudeValues = altitudeData.map((d) => d.value)
      const minAltitude = Math.min(...altitudeValues)
      const maxAltitude = Math.max(...altitudeValues)
      const altitudePadding = (maxAltitude - minAltitude) * 0.1

      altitudeScale = scaleLinear({
        domain: [minAltitude - altitudePadding, maxAltitude + altitudePadding],
        range: [innerHeight, 0],
      })
    }

    // Gap between bars for visual separation
    const gapSize = 2

    return (
      <>
        {/** biome-ignore lint/a11y/noSvgWithoutTitle: title conflicts with tooltip */}
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Altitude gradient definition */}
          {altitudeData.length > 0 && (
            <LinearGradient
              id="splits-altitude-gradient"
              from="#9CA3AF"
              to="#9CA3AF"
              fromOpacity={0.5}
              toOpacity={0.2}
              vertical
            />
          )}

          <Group left={margin.left} top={margin.top}>
            {/* Altitude area in background */}
            {altitudeData.length > 0 && altitudeScale && (
              <AreaClosed
                data={altitudeData}
                x={(d) => xScale(d.timestamp)}
                y={(d) => altitudeScale(d.value)}
                yScale={altitudeScale}
                fill="url(#splits-altitude-gradient)"
                stroke="#9CA3AF"
                strokeWidth={0}
              />
            )}

            {/* Grid */}
            <GridRows scale={yScale} width={innerWidth} stroke="#E5E7EB" strokeOpacity={0.5} />

            {/* Bars for each split */}
            {splits.map((split) => {
              const barX = xScale(split.startTime) + gapSize
              const barWidth = Math.max(xScale(split.endTime) - xScale(split.startTime) - gapSize * 2, 1)
              const barHeight = innerHeight - yScale(split.pace)
              const barY = yScale(split.pace)

              return (
                <Bar
                  key={split.splitNumber}
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={split.hrZoneColor || '#71CCAD'}
                  rx={4}
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(event) => {
                    const point = localPoint(event)
                    showTooltip({
                      tooltipData: split,
                      tooltipLeft: point?.x,
                      tooltipTop: point?.y,
                    })
                  }}
                  onMouseLeave={() => {
                    hideTooltip()
                  }}
                />
              )
            })}

            {/* Axes */}
            <AxisBottom
              top={innerHeight}
              scale={xScale}
              stroke={theme.colors.textSecondary}
              tickStroke={theme.colors.textSecondary}
              numTicks={5}
              tickFormat={(value) => formatTimeSeconds(Number(value))}
              tickLabelProps={() => ({
                fill: theme.colors.textSecondary,
                fontSize: 10,
                textAnchor: 'middle',
              })}
              label="Time"
              labelProps={{
                fill: theme.colors.text,
                fontSize: 12,
                textAnchor: 'middle',
              }}
            />
            <AxisLeft
              scale={yScale}
              stroke={theme.colors.textSecondary}
              tickStroke={theme.colors.textSecondary}
              tickFormat={(value) => formatPace(Number(value))}
              tickLabelProps={() => ({
                fill: theme.colors.textSecondary,
                fontSize: 10,
                textAnchor: 'end',
                dx: -4,
              })}
              label="Pace (min/km)"
              labelProps={{
                fill: theme.colors.text,
                fontSize: 12,
                textAnchor: 'middle',
              }}
            />
          </Group>
        </svg>

        {/* Tooltip */}
        {tooltipOpen && tooltipData && (
          <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
            <TooltipContent split={tooltipData} />
          </TooltipWithBounds>
        )}
      </>
    )
  }

  return (
    <Container ref={parentRef} style={{ minHeight: '250px' }}>
      {renderContent()}
    </Container>
  )
}
